import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../../context/UserContext";
import { useAgreement } from "../../../context/AgreementContext";
import { useUserId, useUserRole, formatDateString } from "../../../util_functions/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import {
  FaUserTie,
  FaClipboardList,
  FaRegCalendarAlt,
  FaDollarSign,
  FaFileAlt,
} from "react-icons/fa";
import { LuClipboardList } from "react-icons/lu";
import AgreementSuccessModal from "./AgreementSuccessModal";
import config from "../../../config/config";
import axios from "axios";

const generateUniqueId = () => Date.now();

const AgreementReviewComponent = () => {
  const { workerUser } = useUser();
  const userRole = useUserRole();    
const workerId   = useUserId();
  const { state: agreementDetails } = useLocation();
  const navigate = useNavigate();

const {
    getByUserAndAgreement,
   createAgreement,
   updateAgreement,
  } = useAgreement();

  const [agreementId, setAgreementId] = useState(null);
  const [isExistingAgreement, setIsExistingAgreement] = useState(false);
  const [agreementDate, setAgreementDate] = useState(null);
  const [editButtonText, setEditButtonText] = useState("Edit Agreement");
  const [editButtonDisabled, setEditButtonDisabled] = useState(false);

  const {
    messageId,
    clientId,
    clientName,
    creatorUserType=userRole,
    jobId,
    jobFrequency,
    sessions,
    hoursPerWeek,
    selectedJobStartDate,
    sessionStartDate,
    sessionEndDate,
    workerName,
    jobLocation,
    jobTitle,
    jobDescription,
    totalAgreementHours,
    rates,
    offeredRateTypes,
    preferredDays = {},
    timeSlots = {},
    noFeeForInitialGreetingSession,
    initialGreetingSessionRate,
    includeTravelRate,
    travelRatePerMile,
    editorContent,
    workerSigned,
    clientSigned,
    status,
  } = agreementDetails;


  useEffect(() => {
  const initializeAgreement = async () => {
    let existing = null;
    try {
      existing = await getByUserAndAgreement(
        userRole,
        workerId,
        agreementDetails.agreementId
      );
      setIsExistingAgreement(true);
    } catch (err) {
      if (err.response?.status === 404) {
        setIsExistingAgreement(false);
        existing = null;
      } else {
        console.error("Error fetching existing agreement:", err);
        return;
      }
    }

    setAgreementId(existing ? existing.agreementId : generateUniqueId());
    setAgreementDate(existing?.agreementDate ?? Date.now());

    if (workerUser.userType === "worker") {
      if (agreementDetails.workerSigned && !agreementDetails.clientSigned) {
        setEditButtonText("Edit Agreement");
        setEditButtonDisabled(false);
      } else if (
        agreementDetails.workerSigned &&
        agreementDetails.clientSigned
      ) {
        setEditButtonText("Agreement Signed & Locked");
        setEditButtonDisabled(true);
      }
    }
  };

  initializeAgreement();
}, [
  getByUserAndAgreement,
  userRole,
  workerId,
  agreementDetails.agreementId,
  workerUser.userType,
  agreementDetails.workerSigned,
  agreementDetails.clientSigned,
]);

  const handleEditAgreement = () => {
    navigate("/account/ps-dashboard/agreement-setup", { state: agreementDetails });
  };

  const renderAdditionalConsiderations = () => {
    if (editorContent.trim() !== "") {
      return (
        <div className="agreement-travel">
          <h3>
            <FaFileAlt className="icon-common icon-file" />
            Additional Considerations
          </h3>
          <div
            className="agreement-review-editor-content"
            dangerouslySetInnerHTML={{ __html: editorContent }}
          ></div>
        </div>
      );
    }
    return (
      <div className="agreement-travel">
        <h3>
          <FaFileAlt className="icon-common icon-file" />
          Additional Considerations
        </h3>
        <p>N/A</p>
      </div>
    );
  };

  const handleSaveAndSubmitAgreement = async () => {
  const payload = {
    ...agreementDetails,
  };

  console.log("🚀 payload for save:", payload);

  try {
    if (isExistingAgreement) {
      await updateAgreement(agreementId, payload);
      console.log("✅ Agreement updated");
    } else {
      await createAgreement(payload);
      console.log("✅ Agreement created");
    }

    await sendConfirmationMessage();
  } catch (err) {
    console.error("❌ Error saving agreement via context:", err);
  }
};

  const sendConfirmationMessage = async () => {
    const messageText = `Agreement has been submitted and ready for review.`;
    await sendOutgoingMessage(messageText);
  };

  const sendOutgoingMessage = async (text) => {
    if (!text.trim()) return;

    const newFullMessage = {
      text,
      timestamp: new Date().toISOString(),
      sender: `${workerUser.userType}`,
      receiver: "client",
      jobId: jobId,
      read: false,
    };

    try {
      const messageId = agreementDetails.messageId;
      await axios.post(
        `${config.API_BASE_URL}/v1/messages/addChatMessage/${messageId}`,
        newFullMessage
      );
      console.log("Message sent successfully:");
    } catch (error) {
      console.error("Error sending the message:", error);
    }
  };

  return (
    <div className="agreement-setup-component-container">
      <div className="agreement-header-edit-btn">
        <h2>
          Agreement Review <FontAwesomeIcon icon={faCheckCircle} className="icon-common" />
        </h2>
        <button
          className="agreement-edit-button"
          onClick={handleEditAgreement}
          disabled={editButtonDisabled}
        >
          <FontAwesomeIcon className="icon-common" icon={faEdit} />
          {editButtonText}
        </button>
      </div>

      <div className="agreement-job-details">
        <h3>
          <FaUserTie className="icon-common icon-user" />
          Parties to the Agreement
        </h3>
        <p>
          This agreement is entered into by&nbsp;
          <span><strong>{`${workerUser.firstName} ${workerUser.lastName}`}</strong></span>&nbsp;
          hereinafter referred to as <strong>Worker</strong>, and &nbsp;
          <span><strong>{clientName}</strong></span>&nbsp; hereinafter referred to as
          <strong>Client</strong>.
        </p>

        <div className="agreement-travel">
          <h3>
            <LuClipboardList className="icon-common icon-list" />
            Job details
          </h3>

          <p>
            Title <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobTitle}</span>
            </strong>
          </p>
          <p>
            Location <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobLocation}</span>
            </strong>
          </p>
          <p>
            Job Frequency <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobFrequency}</span>
            </strong>
          </p>
          <p>
            Sessions / Week <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{sessions}</span>&nbsp;
            </strong>
          </p>
          <p>
            Hours / Session <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{hoursPerWeek}</span>&nbsp;
            </strong>
          </p>
          <p>
            Start Date <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{selectedJobStartDate}</span>
            </strong>
          </p>
        </div>
        <div className="agreement-travel">
          <h3><LuClipboardList className="icon-common icon-list" />
            Job Description</h3>

          <p>{jobDescription}</p>

        </div>
      </div>

      <div className="agreement-travel">
        <h3><FaRegCalendarAlt className="icon-common icon-list" /> Agreement Days & Time Slots</h3>
        <p>Client’s requested support schedule below:</p>
      </div>
      <div className="agreement-rates-display">
        {Object.entries(preferredDays)
          .filter(([_, isPreferred]) => isPreferred)
          .map(([day]) => (
            <div key={day} className="rate-item">
              <strong>{day}<span className="user-dashboard-arrow">➤</span></strong>{" "}
              {(timeSlots[day] || []).map((slot, i) => (
                <span key={i} className="timeslot-span">
                  {slot.from} - {slot.to}
                  {i < timeSlots[day].length - 1 ? ", " : ""}
                </span>
              ))}
            </div>
          ))}
        {Object.values(preferredDays).every(v => !v) && (
          <p>No preferred days selected.</p>
        )}
      </div>
      <div className="agreement-travel">
        <p>
          Total Agreement Hours <span className="user-dashboard-arrow">➤</span><span><strong>{totalAgreementHours}</strong></span>
        </p>
      </div>
      <div className="agreement-travel">
        <h3>
          <FaDollarSign className="icon-common icon-dollar" />
          Agreed Rates
        </h3>
        {Object.entries(rates).map(([type, rate], index) => {
          if (agreementDetails.offeredRateTypes.includes(type)) {
            const originalRate = parseFloat(rate);
            const reducedRate = (originalRate * 0.93).toFixed(2);
            return (
              <div key={index} className="rate-agreement-review-content">
                <div className="rate-item">
                  {type}<span className="user-dashboard-arrow">➤</span> <strong>${rate}</strong>
                </div>
                <div className="net-rate-note">
                  Net pay after <strong>7%</strong>&nbsp;
                  Platform fee <span className="user-dashboard-arrow">➤</span> <strong>${reducedRate}</strong>
                </div>
              </div>
            );
          }
          return null;
        })}
        <div className="rate-agreement-review-content">
          <div className="rate-item">
            Meet &amp; Greet Session fee<span className="user-dashboard-arrow">➤</span>{" "}
            <strong>
              {noFeeForInitialGreetingSession ? "Free" : `$${initialGreetingSessionRate}`}
            </strong>
          </div>
          {!noFeeForInitialGreetingSession && (
            <div className="net-rate-note">
              Net pay after <strong>7%</strong> Platform fee{" "}
              <span className="user-dashboard-arrow">➤</span>{" "}
              <strong>
                $
                {(parseFloat(initialGreetingSessionRate) * 0.93).toFixed(2)}
              </strong>
            </div>
          )}
          {includeTravelRate && travelRatePerMile && (
            <div className="rate-item">
              Travel Rate per Mile
              <span className="user-dashboard-arrow">➤</span>
              <strong>${travelRatePerMile}</strong>
            </div>
          )}
        </div>
      </div>

      {renderAdditionalConsiderations()}
      <div className="agreement-travel">
        <h3>
          <FaFileAlt className="icon-common icon-file" />
          Referrence Details
        </h3>
        <div className="agreement-references">
          <p>
            Agreement Ref <span className="user-dashboard-arrow">➤</span> <span><strong>{agreementId}</strong></span>
          </p>
          <p>
            Date and Time<span className="user-dashboard-arrow">➤</span>{formatDateString(agreementDate)}
          </p>
          <p>
            Job Ref <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobId}</span>
            </strong>
          </p>
          <p>
            Client Ref <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{clientId}</span>
            </strong>
          </p>
          <p>
            Worker Ref <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{workerId}</span>
            </strong>
          </p>
        </div>
      </div>
      <div className="agreement-review-buttons-container">
        <button
          className="agreement-review-button"
          onClick={handleSaveAndSubmitAgreement}
        >
          Agree & Submit
        </button>
      </div>
    </div>
  );
};

export default AgreementReviewComponent;
